import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import {
  FOUR_PILLARS,
  PILLAR_WEIGHT_SEGMENTS,
} from "../../utils/fourPillarsConfig";
import { cn } from "../../lib/utils";

const SCORE_RANGES = [
  { label: "Excellent", range: "80 – 100", color: "text-emerald-600" },
  { label: "Good", range: "60 – 79", color: "text-blue-600" },
  { label: "Caution", range: "40 – 59", color: "text-orange-600" },
  { label: "Poor", range: "0 – 39", color: "text-red-600" },
] as const;

const FourPillarsDetailPage = () => {
  const navigate = useNavigate();

  return (
    <div className="scrollbar-hide relative mx-auto flex h-dvh max-h-dvh w-full min-w-0 max-w-[400px] flex-col overflow-hidden bg-background">
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/85" />

      <div className="scrollbar-hide relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-24 pt-3">
        <div className="mb-5 flex items-start gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="mt-0.5 h-10 w-10 shrink-0 rounded-full border border-border bg-card shadow-sm"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1 pt-1">
            <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Daily Balance
            </p>
            <h1 className="text-title2 font-bold tracking-tight text-foreground">
              Your 4 Pillars
            </h1>
          </div>
        </div>

        <Card className="mb-4 rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
          <p className="text-sm leading-relaxed text-foreground">
            The{" "}
            <span className="font-semibold text-blue-600 dark:text-blue-400">
              Daily Balance
            </span>{" "}
            blends four areas of your health into one score from 0–100.
          </p>
          <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
            Each pillar is weighted by how strongly it affects your glucose
            stability — the steadier your blood sugar, the better your body
            handles every meal.
          </p>
        </Card>

        <div className="space-y-4">
          {FOUR_PILLARS.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <Card
                key={pillar.key}
                className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                      pillar.iconBg,
                    )}
                  >
                    <Icon className={cn("h-5 w-5", pillar.iconColor)} strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-bold text-foreground">
                        {pillar.label}
                      </h2>
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-2xs font-semibold",
                          pillar.badgeBg,
                          pillar.badgeText,
                        )}
                      >
                        {pillar.weightPercent}% of score
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {pillar.subtitle}
                    </p>
                  </div>
                </div>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {pillar.description}
                </p>

                {pillar.bullets ? (
                  <ul className="mt-3 space-y-2">
                    {pillar.bullets.map((item) => (
                      <li
                        key={item.title}
                        className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"
                      >
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                        <span>
                          <span className="font-semibold text-foreground">
                            {item.title}
                          </span>{" "}
                          — {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Card>
            );
          })}

          <Card className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
            <h2 className="text-base font-bold text-foreground">
              How they combine
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Your four pillars blend into one Daily Balance score, weighted
              like this:
            </p>

            <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full">
              {PILLAR_WEIGHT_SEGMENTS.map((segment) => (
                <div
                  key={segment.label}
                  className={cn(segment.color, "h-full")}
                  style={{ width: `${segment.percent}%` }}
                  aria-hidden
                />
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
              {PILLAR_WEIGHT_SEGMENTS.map((segment) => (
                <div key={segment.label} className="flex items-center gap-2">
                  <span
                    className={cn("h-2.5 w-2.5 shrink-0 rounded-full", segment.color)}
                  />
                  <span className="text-sm text-foreground">
                    {segment.label}{" "}
                    <span className="text-muted-foreground">
                      {segment.percent}%
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
            <h2 className="text-base font-bold text-foreground">Score ranges</h2>
            <div className="mt-3 divide-y divide-border/50">
              {SCORE_RANGES.map((range) => (
                <div
                  key={range.label}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <span className="text-sm font-medium text-foreground">
                    {range.label}
                  </span>
                  <span className={cn("text-sm font-semibold", range.color)}>
                    {range.range}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <BottomNavBar activeIndex={0} />
    </div>
  );
};

export default FourPillarsDetailPage;
